import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { resolveApiMessage, showToast } from '../../../helpers';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import { searchOpsAccounts } from '../../../requests';
import {
  getHubspotBridge,
  getHubspotHandoffs,
  getHubspotStatus,
  runHubspotHandoffs,
  runHubspotSync,
  setHubspotBridge,
  TBridgeRow,
  THandoffResult,
} from '../../../requests/opsHubspot';
import {
  btnGhost,
  btnPrimary,
  btnSecondary,
  Card,
  EmptyState,
  inputClass,
  PageBody,
  PageHeader,
  Pill,
  tableClass,
  tdClass,
  theadClass,
  thClass,
} from '../ui';
import { ImportCard, OpportunityMirror } from './ImportCard';

const when = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—';

const money = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const handoffSummary = (h: THandoffResult | null | undefined) =>
  !h
    ? null
    : h.ran === false
    ? h.reason ?? 'Handoff did not run'
    : `${h.created} program${h.created === 1 ? '' : 's'} opened · ${
        h.addOns
      } add-on${h.addOns === 1 ? '' : 's'} · ${
        h.waitingOnBridge
      } waiting on a bridge · ${h.beforeStart} closed before the start date`;

/** One HubSpot company awaiting a decision: accept the suggestion, or pick an account. */
const BridgeRow: React.FC<{
  row: TBridgeRow;
  busy: boolean;
  onSet: (accountId: string | null) => void;
}> = ({ row, busy, onSet }) => {
  const [picking, setPicking] = useState(false);
  const [q, setQ] = useState('');
  const { data: hits = [] } = useQuery({
    queryKey: ['opsAccounts', q],
    queryFn: () => searchOpsAccounts(q),
    enabled: picking && q.trim().length >= 2,
  });
  return (
    <tr>
      <td className={tdClass}>
        <div className="font-medium text-gray-five">{row.hsName ?? '—'}</div>
        {row.hsDomain && (
          <div className="font-mono text-xs text-gray-bold">{row.hsDomain}</div>
        )}
      </td>
      <td className={tdClass}>
        {row.accountId ? (
          <Link
            className="text-blue-primary hover:underline"
            to={PATH.opsAccountView.replace(':id', row.accountId)}
          >
            {row.accountName}
          </Link>
        ) : row.suggestedName ? (
          <>
            <span className="text-gray-five">{row.suggestedName}</span>
            <span className="ml-2 text-xs text-gray-bold">
              suggested · {row.method} · {Math.round(row.confidence * 100)}%
            </span>
          </>
        ) : (
          <span className="text-gray-two">no match found</span>
        )}
      </td>
      <td className={tdClass}>
        {row.reviewedBy ? (
          <span className="text-xs text-gray-bold">
            {row.reviewedBy} · {when(row.reviewedAt)}
          </span>
        ) : row.method && row.accountId ? (
          <Pill value="auto" tone="info" />
        ) : (
          <Pill value="needs review" tone="warn" />
        )}
      </td>
      <td className={`${tdClass} text-right`}>
        {picking ? (
          <div className="inline-block w-72 text-left">
            <input
              autoFocus
              className={inputClass}
              placeholder="Search accounts…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            {hits.length > 0 && (
              <div className="mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-white-regular bg-white shadow-light">
                {hits.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-blue-100/60"
                    onClick={() => {
                      setPicking(false);
                      setQ('');
                      onSet(a.id);
                    }}
                  >
                    {a.name}
                    {(a.type || a.city) && (
                      <span className="text-xs text-gray-bold">
                        {' · ' + [a.type, a.city].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
            <button
              className={`${btnGhost} mt-1`}
              onClick={() => {
                setPicking(false);
                setQ('');
              }}
            >
              cancel
            </button>
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            {!row.accountId && row.suggestedAccountId && (
              <button
                className={btnPrimary}
                disabled={busy}
                onClick={() => onSet(row.suggestedAccountId)}
              >
                Accept
              </button>
            )}
            <button
              className={btnSecondary}
              disabled={busy}
              onClick={() => setPicking(true)}
            >
              {row.accountId ? 'Change' : 'Pick account'}
            </button>
            {row.accountId && (
              <button
                className={btnGhost}
                disabled={busy}
                onClick={() => onSet(null)}
              >
                Unlink
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
};

const OpsHubspot: React.FC = () => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'unmatched' | 'all'>('unmatched');
  const { data: status } = useQuery({
    queryKey: ['opsHubspot', 'status'],
    queryFn: getHubspotStatus,
  });
  const { data: bridge, isPending: bridgePending } = useQuery({
    queryKey: ['opsHubspot', 'bridge', tab],
    queryFn: () => getHubspotBridge(tab),
  });
  const { data: handoffs } = useQuery({
    queryKey: ['opsHubspot', 'handoffs'],
    queryFn: getHubspotHandoffs,
  });
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['opsHubspot'] });

  const setBridge = useMutation({
    mutationFn: ({ id, accountId }: { id: string; accountId: string | null }) =>
      setHubspotBridge(id, accountId),
    onSuccess: (r) => {
      const h = handoffSummary(r.handoff);
      showToast(
        r.accountId
          ? `Bridged to ${r.accountName}${h ? ` · ${h}` : ''}`
          : 'Bridge cleared',
        'success',
      );
      refresh();
    },
    onError: (e) => showToast(resolveApiMessage(e), 'error'),
  });
  const runHandoff = useMutation({
    mutationFn: runHubspotHandoffs,
    onSuccess: (r) => {
      showToast(
        handoffSummary(r) ?? 'Done',
        r.ran === false ? 'error' : 'success',
      );
      refresh();
    },
    onError: (e) => showToast(resolveApiMessage(e), 'error'),
  });
  const sync = useMutation({
    mutationFn: runHubspotSync,
    onSuccess: (r) => {
      showToast(
        r.ran ? 'Synced' : r.reason ?? 'Nothing ran',
        r.ran ? 'success' : 'error',
      );
      refresh();
    },
    onError: (e) => showToast(resolveApiMessage(e), 'error'),
  });

  const busy = setBridge.isPending || runHandoff.isPending || sync.isPending;
  // A HubSpot deal's "Salesforce history" link lands here with ?opp=<id>.
  const oppId = new URLSearchParams(window.location.search).get('opp');

  return (
    <>
      <PageHeader
        title="HubSpot"
        meta="The sales system after the move. Deals, companies and contacts are mirrored here read-only; a won deal opens a draft program and its kickoff and scheduling cases once its company is bridged to an account."
      />
      <PageBody>
        {oppId && <OpportunityMirror id={oppId} />}
        {status && (
          <div
            className={`mb-6 rounded-xl border px-5 py-4 ${
              status.mode === 'live' && !status.configured
                ? 'border-red-300 bg-red-50'
                : 'border-white-regular bg-white'
            }`}
          >
            <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-two">
                  Connection
                </div>
                <div className="text-sm text-gray-five">
                  <Pill
                    value={
                      status.mode === 'off'
                        ? 'off'
                        : status.configured
                        ? 'live'
                        : 'no token'
                    }
                    tone={
                      status.mode === 'off'
                        ? 'neutral'
                        : status.configured
                        ? 'good'
                        : 'bad'
                    }
                  />
                  {status.mode === 'live' && status.configured && (
                    <span className="ml-2 text-gray-bold">
                      polls every {status.every}
                    </span>
                  )}
                  {status.mode === 'off' && (
                    <span className="ml-2 text-gray-bold">
                      nothing polls until the private app token is set
                    </span>
                  )}
                </div>
              </div>
              {(
                [
                  ['Deals', status.counts.deals],
                  ['Companies', status.counts.companies],
                  ['Contacts', status.counts.contacts],
                  ['Bridged', status.counts.bridged],
                  ['To review', status.counts.unmatched],
                  ['Handoffs', status.counts.handoffs],
                ] as const
              ).map(([label, n]) => (
                <div key={label}>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-two">
                    {label}
                  </div>
                  <div className="text-lg font-semibold text-gray-five">
                    {n.toLocaleString()}
                  </div>
                </div>
              ))}
              <div className="ml-auto flex gap-2">
                <button
                  className={btnSecondary}
                  disabled={
                    busy || status.mode !== 'live' || !status.configured
                  }
                  onClick={() => sync.mutate()}
                >
                  Sync now
                </button>
                <button
                  className={btnPrimary}
                  disabled={busy}
                  onClick={() => runHandoff.mutate()}
                >
                  Open programs for won deals
                </button>
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-bold">
              Handoff start date:{' '}
              {status.handoffSince ? (
                <span className="text-gray-five">
                  deals closed on or after {status.handoffSince}
                </span>
              ) : (
                <span className="text-red-700">
                  not set — no deal is handed off until HUBSPOT_HANDOFF_SINCE is
                  set, so historical won deals never reopen their programs
                </span>
              )}
            </div>
            {status.recentRuns.length > 0 && (
              <div className="mt-2 text-xs text-gray-bold">
                Last run {when(status.recentRuns[0].at)} ·{' '}
                {status.recentRuns[0].ok
                  ? `${status.recentRuns[0].deals ?? 0} deals · ${
                      status.recentRuns[0].companies ?? 0
                    } companies · ${
                      status.recentRuns[0].contacts ?? 0
                    } contacts`
                  : status.recentRuns[0].error}
              </div>
            )}
          </div>
        )}

        <Card
          title="Company ↔ account bridge"
          right={
            <div className="flex gap-1">
              {(['unmatched', 'all'] as const).map((t) => (
                <button
                  key={t}
                  className={
                    tab === t
                      ? 'rounded-lg bg-blue-100 px-3 py-1.5 text-xs font-semibold text-blue-primary'
                      : 'rounded-lg px-3 py-1.5 text-xs text-gray-three hover:bg-grey-50'
                  }
                  onClick={() => setTab(t)}
                >
                  {t === 'unmatched' ? 'To review' : 'All'}
                </button>
              ))}
            </div>
          }
        >
          {bridgePending || !bridge ? (
            <LoadingSpinner />
          ) : bridge.length === 0 ? (
            <EmptyState
              title={
                tab === 'unmatched'
                  ? 'Nothing to review'
                  : 'No HubSpot companies mirrored yet'
              }
              hint={
                tab === 'unmatched'
                  ? 'A company lands here when its name and domain do not match an account well enough to bridge on their own.'
                  : undefined
              }
            />
          ) : (
            <table className={tableClass}>
              <thead className={theadClass}>
                <tr>
                  <th className={thClass}>HubSpot company</th>
                  <th className={thClass}>Account</th>
                  <th className={thClass}>Decided by</th>
                  <th className={`${thClass} text-right`} />
                </tr>
              </thead>
              <tbody>
                {bridge.map((row) => (
                  <BridgeRow
                    key={row.id}
                    row={row}
                    busy={busy}
                    onSet={(accountId) =>
                      setBridge.mutate({ id: row.id, accountId })
                    }
                  />
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card
          title="Handoffs — won deals turned into programs"
          className="mt-6"
        >
          {!handoffs ? (
            <LoadingSpinner />
          ) : handoffs.length === 0 ? (
            <EmptyState
              title="No handoffs yet"
              hint="Each won deal appears here once, with the program and cases it opened."
            />
          ) : (
            <table className={tableClass}>
              <thead className={theadClass}>
                <tr>
                  <th className={thClass}>When</th>
                  <th className={thClass}>Deal</th>
                  <th className={thClass}>Kind</th>
                  <th className={thClass}>Program</th>
                  <th className={`${thClass} text-right`}>Amount</th>
                  <th className={thClass}>By</th>
                </tr>
              </thead>
              <tbody>
                {handoffs.map((h) => (
                  <tr key={h.dealId}>
                    <td className={`${tdClass} whitespace-nowrap`}>
                      {when(h.at)}
                    </td>
                    <td className={`${tdClass} font-mono text-xs`}>
                      {h.dealId}
                    </td>
                    <td className={tdClass}>
                      <Pill
                        value={
                          h.kind === 'program'
                            ? 'new program'
                            : h.kind === 'add-on'
                            ? 'add-on'
                            : 'add-on · unplaced'
                        }
                        tone={h.kind === 'add-on-unplaced' ? 'warn' : 'info'}
                      />
                    </td>
                    <td className={tdClass}>
                      {h.programId ? (
                        <Link
                          className="text-blue-primary hover:underline"
                          to={`${PATH.opsPrograms}?q=${encodeURIComponent(
                            h.programId,
                          )}`}
                        >
                          {h.programId}
                        </Link>
                      ) : (
                        <span className="text-gray-two">
                          no active program to raise
                        </span>
                      )}
                      {h.caseIds && h.caseIds.length > 0 && (
                        <span className="ml-2 text-xs text-gray-bold">
                          + {h.caseIds.length} cases
                        </span>
                      )}
                    </td>
                    <td className={`${tdClass} text-right`}>
                      {money(h.amount)}
                    </td>
                    <td className={`${tdClass} text-xs text-gray-bold`}>
                      {h.by}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
        <ImportCard />
      </PageBody>
    </>
  );
};

export default OpsHubspot;
